import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAssistance, useChimeMeeting } from "../hooks";

export const MicrophoneContext = React.createContext({});

/**
 * The microphone context provider
 * 
 * @param {Object} props The props of the component
 * @returns The microphone context provider
 */
const MicrophoneProvider = ({ children }) => {
  const { assistance } = useAssistance();

  const microphones = useMemo(
    () => assistance?.available_speakers,
    [assistance],
  );

  const [selectedMicrophone, setSelectedMicrophone] = useState(
    microphones?.[0],
  );
  const [isMuted, setIsMuted] = useState(false);

  const { sendControlMessage, muteLocalAudio, unMuteLocalAudio } =
    useChimeMeeting();

  /**
   * Checks if a microphone is selected
   * 
   * @param {String} microphone The microphone ID
   * @returns {Boolean} True if the microphone is selected, false otherwise
   */
  const isMicrophoneSelected = useCallback(
    (microphone) => {
      return microphone === selectedMicrophone;
    },
    [selectedMicrophone],
  );

  /**
   * Unmutes the microphone
   */
  const unMuteMicrophone = useCallback(() => {
    if (isMuted) {
      setIsMuted(false);
      unMuteLocalAudio();
    }
  }, [isMuted, unMuteLocalAudio]);

  /**
   * Handles the microphone selection
   * 
   * @param {String} speaker The speaker ID
   */
  const onSelectMicrophone = useCallback(
    (speaker) => {
      unMuteMicrophone();
      setSelectedMicrophone(speaker);
    },
    [unMuteMicrophone],
  );

  /**
   * Resets the microphone
   */
  const resetMicrophone = useCallback(() => {
    onSelectMicrophone(microphones?.[0]);
  }, [microphones, onSelectMicrophone]);

  /**
   * Mutes the microphone
   */
  const onMuteMicrophone = useCallback(() => {
    if (!isMuted) {
      setIsMuted(true);
      muteLocalAudio();
    }
  }, [isMuted, muteLocalAudio]);

  useEffect(() => {
    sendControlMessage(
      JSON.stringify({
        mic: selectedMicrophone?.name,
      }),
    );
  }, [selectedMicrophone, sendControlMessage]);

  const contextValue = useMemo(
    () => ({
      isMuted,
      onMuteMicrophone,
      unMuteMicrophone,
      resetMicrophone,
      selectedMicrophone,
      microphones,
      onSelectMicrophone,
      isMicrophoneSelected,
    }),
    [
      isMuted,
      onMuteMicrophone,
      unMuteMicrophone,
      resetMicrophone,
      selectedMicrophone,
      microphones,
      onSelectMicrophone,
      isMicrophoneSelected,
    ],
  );

  return (
    <MicrophoneContext.Provider value={contextValue}>
      {children}
    </MicrophoneContext.Provider>
  );
};

export default React.memo(MicrophoneProvider);
